import { CLASSES, PROFESSIONS, ITEM_QUALITY_NAMES, type ArtifactType } from '@thottbot/shared';

/**
 * Declarative form fields per artifact type. Paths are dotted keys into `data`.
 * Kinds:
 *  - text/number/textarea/select: scalar values
 *  - ref: a single `{name}` reference (giver, turnIn, prevQuest, nextQuest)
 *  - refs: a list of `{name}` references, entered one per line
 *  - stats: "Stamina 5" one per line -> [{name, value}]
 *  - reagents: "2 x Linen Cloth" one per line -> [{name, count}]
 *  - damage: min/max/speed triple
 */
export type FieldKind =
  | 'text'
  | 'number'
  | 'textarea'
  | 'select'
  | 'ref'
  | 'refs'
  | 'stats'
  | 'reagents'
  | 'damage'
  | 'zone';

export interface FieldDef {
  path: string;
  label: string;
  kind: FieldKind;
  options?: readonly string[];
  /** For number fields. */
  min?: number;
  max?: number;
  help?: string;
}

const desc: FieldDef = { path: 'description', label: 'Description', kind: 'textarea' };

export const FORM_FIELDS: Record<ArtifactType, FieldDef[]> = {
  quest: [
    { path: 'level', label: 'Level', kind: 'number', min: 0, max: 70 },
    { path: 'minLevel', label: 'Requires level', kind: 'number', min: 0, max: 70 },
    { path: 'zone', label: 'Zone', kind: 'zone' },
    { path: 'faction', label: 'Faction', kind: 'select', options: ['Alliance', 'Horde', 'Both'] },
    { path: 'giver', label: 'Starts at (NPC or object)', kind: 'ref' },
    { path: 'turnIn', label: 'Ends at (NPC or object)', kind: 'ref' },
    { path: 'objectives', label: 'Objectives', kind: 'textarea' },
    desc,
    { path: 'rewards.xp', label: 'Experience', kind: 'number', min: 0 },
    {
      path: 'rewards.money',
      label: 'Money (copper)',
      kind: 'number',
      min: 0,
      help: '1 gold = 10000 copper',
    },
    { path: 'rewards.items', label: 'Reward items', kind: 'refs', help: 'One item name per line' },
    {
      path: 'rewards.choiceItems',
      label: 'Choose one of',
      kind: 'refs',
      help: 'One item name per line',
    },
    { path: 'prevQuest', label: 'Previous quest', kind: 'ref' },
    { path: 'nextQuest', label: 'Next quest', kind: 'ref' },
  ],
  item: [
    { path: 'quality', label: 'Quality', kind: 'select', options: ITEM_QUALITY_NAMES },
    { path: 'itemClass', label: 'Class', kind: 'text', help: 'Weapon, Armor, Consumable, ...' },
    { path: 'subclass', label: 'Subclass', kind: 'text', help: 'Sword, Cloth, Potion, ...' },
    { path: 'slot', label: 'Slot', kind: 'text' },
    { path: 'itemLevel', label: 'Item level', kind: 'number', min: 0 },
    { path: 'requiredLevel', label: 'Requires level', kind: 'number', min: 0, max: 70 },
    {
      path: 'bind',
      label: 'Binds',
      kind: 'select',
      options: ['None', 'BoP', 'BoE', 'BoU', 'Quest'],
    },
    { path: 'damage', label: 'Damage', kind: 'damage' },
    { path: 'armor', label: 'Armor', kind: 'number', min: 0 },
    { path: 'stats', label: 'Stats', kind: 'stats', help: 'One per line, e.g. "Stamina 5"' },
    { path: 'sellPrice', label: 'Sells for (copper)', kind: 'number', min: 0 },
    desc,
    { path: 'droppedBy', label: 'Dropped by', kind: 'refs', help: 'One NPC name per line' },
    { path: 'soldBy', label: 'Sold by', kind: 'refs', help: 'One NPC name per line' },
  ],
  npc: [
    { path: 'levelMin', label: 'Level (min)', kind: 'number', min: 0, max: 70 },
    { path: 'levelMax', label: 'Level (max)', kind: 'number', min: 0, max: 70 },
    { path: 'creatureType', label: 'Type', kind: 'text', help: 'Humanoid, Beast, Undead, ...' },
    { path: 'faction', label: 'Faction', kind: 'text' },
    { path: 'zone', label: 'Zone', kind: 'zone' },
    { path: 'description', label: 'Title / notes', kind: 'textarea' },
    { path: 'startsQuests', label: 'Starts quests', kind: 'refs', help: 'One quest name per line' },
    { path: 'endsQuests', label: 'Ends quests', kind: 'refs', help: 'One quest name per line' },
    { path: 'drops', label: 'Drops', kind: 'refs', help: 'One item name per line' },
    { path: 'sells', label: 'Sells', kind: 'refs', help: 'One item name per line' },
  ],
  object: [
    { path: 'objectType', label: 'Type', kind: 'text', help: 'Chest, Herb, Mineral vein, ...' },
    { path: 'zone', label: 'Zone', kind: 'zone' },
    desc,
    { path: 'contains', label: 'Contains', kind: 'refs', help: 'One item name per line' },
    { path: 'startsQuests', label: 'Starts quests', kind: 'refs' },
    { path: 'endsQuests', label: 'Ends quests', kind: 'refs' },
  ],
  zone: [
    { path: 'continent', label: 'Continent', kind: 'text' },
    { path: 'levelMin', label: 'Level (min)', kind: 'number', min: 0, max: 70 },
    { path: 'levelMax', label: 'Level (max)', kind: 'number', min: 0, max: 70 },
    {
      path: 'faction',
      label: 'Faction',
      kind: 'select',
      options: ['Alliance', 'Horde', 'Contested', 'PvP'],
    },
    desc,
  ],
  ability: [
    { path: 'class', label: 'Class', kind: 'select', options: CLASSES },
    { path: 'tree', label: 'Tree', kind: 'text' },
    { path: 'level', label: 'Level', kind: 'number', min: 0, max: 70 },
    { path: 'rank', label: 'Rank', kind: 'number', min: 0 },
    { path: 'cost', label: 'Cost', kind: 'text' },
    { path: 'castTime', label: 'Cast time', kind: 'text' },
    { path: 'cooldown', label: 'Cooldown', kind: 'text' },
    { path: 'range', label: 'Range', kind: 'text' },
    desc,
  ],
  recipe: [
    { path: 'profession', label: 'Profession', kind: 'select', options: PROFESSIONS },
    { path: 'requiredSkill', label: 'Requires skill', kind: 'number', min: 0 },
    { path: 'resultItemName', label: 'Creates', kind: 'text' },
    {
      path: 'reagents',
      label: 'Reagents',
      kind: 'reagents',
      help: 'One per line, e.g. "2 x Linen Cloth"',
    },
    { path: 'source', label: 'Source', kind: 'text', help: 'Trainer, vendor, drop, ...' },
    desc,
  ],
};

type Data = Record<string, unknown>;

export function getPath(data: Data, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (acc, key) => (acc && typeof acc === 'object' ? (acc as Data)[key] : undefined),
      data,
    );
}

function setPath(data: Data, path: string, value: unknown): void {
  const keys = path.split('.');
  let cur = data;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i]!;
    if (!cur[k] || typeof cur[k] !== 'object') cur[k] = {};
    cur = cur[k] as Data;
  }
  const last = keys[keys.length - 1]!;
  if (value === undefined) delete cur[last];
  else cur[last] = value;
}

interface RefLike {
  name: string;
  [k: string]: unknown;
}

function refsToText(v: unknown): string {
  return Array.isArray(v) ? (v as RefLike[]).map((r) => r.name).join('\n') : '';
}
/** Rebuild a ref list from names, keeping existing ref objects (ids, chance) when the name matches. */
function textToRefs(text: string, existing: unknown): RefLike[] | undefined {
  const names = text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
  if (names.length === 0) return undefined;
  const prior = Array.isArray(existing) ? (existing as RefLike[]) : [];
  return names.map((name) => prior.find((p) => p.name === name) ?? { name });
}

/** Form values are strings keyed by field path. */
export type FormValues = Record<string, string>;

export function dataToValues(type: ArtifactType, data: Data): FormValues {
  const out: FormValues = {};
  for (const f of FORM_FIELDS[type]) {
    const v = getPath(data, f.path);
    switch (f.kind) {
      case 'ref':
        out[f.path] = v && typeof v === 'object' ? String((v as RefLike).name ?? '') : '';
        break;
      case 'refs':
        out[f.path] = refsToText(v);
        break;
      case 'stats':
        out[f.path] = Array.isArray(v)
          ? (v as { name: string; value: number }[]).map((s) => `${s.name} ${s.value}`).join('\n')
          : '';
        break;
      case 'reagents':
        out[f.path] = Array.isArray(v)
          ? (v as { name: string; count: number }[]).map((r) => `${r.count} x ${r.name}`).join('\n')
          : '';
        break;
      case 'damage': {
        const d = v as { min?: number; max?: number; speed?: number } | undefined;
        out[`${f.path}.min`] = d?.min !== undefined ? String(d.min) : '';
        out[`${f.path}.max`] = d?.max !== undefined ? String(d.max) : '';
        out[`${f.path}.speed`] = d?.speed !== undefined ? String(d.speed) : '';
        break;
      }
      case 'select':
        if (f.path === 'quality')
          out[f.path] = typeof v === 'number' ? (ITEM_QUALITY_NAMES[v] ?? '') : '';
        else out[f.path] = v === undefined || v === null ? '' : String(v);
        break;
      default:
        out[f.path] = v === undefined || v === null ? '' : String(v);
    }
  }
  return out;
}

/** Merge form values onto existing data (keys the form does not render are preserved). */
export function valuesToData(type: ArtifactType, values: FormValues, existing: Data = {}): Data {
  const data: Data = structuredClone(existing);
  for (const f of FORM_FIELDS[type]) {
    const raw = values[f.path] ?? '';
    const text = raw.trim();
    switch (f.kind) {
      case 'number':
        setPath(data, f.path, text === '' ? undefined : Number(text));
        break;
      case 'ref': {
        const prior = getPath(existing, f.path) as RefLike | undefined;
        setPath(
          data,
          f.path,
          text === '' ? undefined : prior && prior.name === text ? prior : { name: text },
        );
        break;
      }
      case 'refs':
        setPath(data, f.path, textToRefs(raw, getPath(existing, f.path)));
        break;
      case 'stats': {
        const stats = raw
          .split('\n')
          .map((l) => l.trim().match(/^(.+?)\s+([-+]?\d+)$/))
          .filter((m): m is RegExpMatchArray => !!m)
          .map((m) => ({ name: m[1]!, value: Number(m[2]) }));
        setPath(data, f.path, stats.length ? stats : undefined);
        break;
      }
      case 'reagents': {
        const reagents = raw
          .split('\n')
          .map((l) => l.trim().match(/^(\d+)\s*[xX*]\s*(.+)$/))
          .filter((m): m is RegExpMatchArray => !!m)
          .map((m) => ({ name: m[2]!.trim(), count: Number(m[1]) }));
        setPath(data, f.path, reagents.length ? reagents : undefined);
        break;
      }
      case 'damage': {
        const min = (values[`${f.path}.min`] ?? '').trim();
        const max = (values[`${f.path}.max`] ?? '').trim();
        const speed = (values[`${f.path}.speed`] ?? '').trim();
        setPath(
          data,
          f.path,
          min && max && speed
            ? { min: Number(min), max: Number(max), speed: Number(speed) }
            : undefined,
        );
        break;
      }
      case 'select':
        if (f.path === 'quality') {
          const idx = ITEM_QUALITY_NAMES.indexOf(text as (typeof ITEM_QUALITY_NAMES)[number]);
          setPath(data, f.path, idx >= 0 ? idx : undefined);
        } else setPath(data, f.path, text === '' ? undefined : text);
        break;
      default:
        setPath(data, f.path, text === '' ? undefined : text);
    }
  }
  // Drop empty nested containers (e.g. rewards: {}).
  for (const [k, v] of Object.entries(data)) {
    if (v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v as Data).length === 0)
      delete data[k];
  }
  return data;
}
